// List of card types on the Daily Summary page along with a predicate to enable a card for the user
// Possible predicates are:
// > boolean: sets applicable to the same
// > string: uses the string as a key in the historicalDeviceCapabilitiesIndicators from the usage indicators to get a true/false
// > function: receives historicalDeviceCapabilitiesIndicators and userSettings as params and returns a true/false
// Note, the Activities card is an exception and handled differently
export const CardApplicableConfig = {
    steps: true, // Always shown
    heartRate: 'hasAllDayHeartRateCapableDevice', // Shown if capable device && synced
    intensityMinutes: 'hasIntensityMinuteCapableDevice', // Shown if capable device
    floorsClimbed: 'hasFloorCountCapableDevice', // Shown if capable device
    stress: 'hasAllDayStressCapableDevice', // Shown if capable device && synced
    bodyBattery: 'hasBodyBatteryCapableDevice', // Shown if capable device && synced
    calories: true, // Always shown
    weight: true, // no device capability for this, show only if we have data for that day
    hydration: true, // no device capability for this, Shown if synced
    sleepCoach: 'hasSleepNeedCapableDevice', // Shown if capable device && show only if we have data for that day
    sleepResults: true, // no device capability for this, shown if synced
    sleep: true, // no device capability for this, Shown if synced
    sleepScore: 'hasSleepScoreCapableDevice', // Shown if capable device && synced
    sleepPulseOx: (usageIndicators) => {
        return !usageIndicators.hasPulseOxAllDayCapableDevice && usageIndicators.hasPulseOxSleepCapableDevice;
    }, // Shown if capable device && synced
    respiration: 'hasRespirationCapableDevice', // Shown if capable device && synced
    allDayPulseOx: 'hasPulseOxAllDayCapableDevice', // Shown if capable device && synced
    menstrualCycle: false, // Shown only if we have data for that day
    trainingStatus: 'hasTrainingStatusCapableDevice', //Shown if capable device
    pregnancy: false, // Shown only if we have data for that day
    bloodPressure: true, // no device capability for this, show only if we have data for that day
    trainingReadiness: 'hasTrainingReadinessCapableDevice', // Shown if capable deivce
    hrvStatus: 'hasHrvStatusCapableDevice', //Shown if capable device
    jetLag: false, // Shown only if we have data for that day
    pushes: true //the required conditions to show the card need to be added
};

// Quick config based on the above CardApplicableConfig that has all the card types(except activity and health snapshot)
export const AllCardTypes = Object.keys(CardApplicableConfig);

// These cards show if there's not a sync for the page date
// Note, IMs and Floors and Training Status will only show if the user has a device capable of that feature
export const DefaultCardTypes = [
    'trainingStatus',
    'pushes',
    'steps',
    'intensityMinutes',
    'floorsClimbed',
    'calories',
    'menstrualCycle',
    'pregnancy',
    'weight',
    'bloodPressure',
    'hydration',
    'trainingReadiness',
    'hrvStatus',
    'jetLag'
];

// Goal types enum to match: /goal-service/goal/user/effective/<calendarDate>
export const WellnessGoalTypes = {
    FLOORS_CLIMBED: {
        key: 'FLOORS_CLIMBED',
        urlFriendly: 'floors-climbed'
    },
    INTENSITY_MINUTES: {
        key: 'INTENSITY_MINUTES',
        urlFriendly: 'intensity-minutes'
    },
    HYDRATION: {
        key: 'HYDRATION'
    }
};

export const UserStatsMetricTypes = {
    WELLNESS_TOTAL_STEPS: 'WELLNESS_TOTAL_STEPS',
    WELLNESS_MODERATE_INTENSITY_MINUTES: 'WELLNESS_MODERATE_INTENSITY_MINUTES',
    WELLNESS_VIGOROUS_INTENSITY_MINUTES: 'WELLNESS_VIGOROUS_INTENSITY_MINUTES',
    WELLNESS_FLOORS_ASCENDED: 'WELLNESS_FLOORS_ASCENDED',
    COMMON_TOTAL_CALORIES: 'COMMON_TOTAL_CALORIES',
    WELLNESS_TOTAL_PUSHES: 'WELLNESS_TOTAL_PUSHES'
};

export const UserStatsMetricIds = {
    WELLNESS_TOTAL_STEPS: 29,
    WELLNESS_MODERATE_INTENSITY_MINUTES: 51,
    WELLNESS_VIGOROUS_INTENSITY_MINUTES: 52,
    WELLNESS_FLOORS_ASCENDED: 53,
    COMMON_TOTAL_CALORIES: 41,
    WELLNESS_TOTAL_PUSHES: 87
};

// Return the PAT from the provided list of user devices
export const getPrimaryTracker = (deviceList) => {
    if (deviceList) {
        for (var i = 0, length = deviceList.length; i < length; i++) {
            if (deviceList[i].primaryActivityTrackerIndicator) {
                return deviceList[i];
            }
        }
    }
};

// Add a new card type to the order array(to specify its location in the page) and in the object below with its default enabled/open state.
export const defaultCardSettings = {
    order: [
        'activity',
        'ecgRecording',
        'healthSnapshot',
        'trainingReadiness',
        'trainingStatus',
        'jetLag',
        // 'pregnancy', // Unreleased feature
        'heartRate',
        'bodyBattery',
        'stress',
        'intensityMinutes',
        'pushes',
        'steps',
        'floorsClimbed',
        'calories',
        'sleepCoach',
        'sleepResults',
        'sleep',
        'sleepScore',
        'hrvStatus',
        'sleepPulseOx',
        'allDayPulseOx',
        'respiration',
        'menstrualCycle',
        'weight',
        'floorsClimbed',
        'hydration',
        'menstrualCycle',
        'pregnancy',
        'bloodPressure',
        'event'
    ],
    activity: {
        enabled: true
    },
    ecgRecording: {
        enabled: true
    },
    healthSnapshot: {
        enabled: true
    },
    trainingStatus: {
        enabled: true
    },
    steps: {
        enabled: true,
        open: false
    },
    heartRate: {
        enabled: true,
        open: false
    },
    intensityMinutes: {
        enabled: true,
        open: false
    },
    floorsClimbed: {
        enabled: true
    },
    stress: {
        enabled: true,
        open: false
    },
    bodyBattery: {
        enabled: true,
        open: false
    },
    calories: {
        enabled: true,
        open: false
    },
    weight: {
        enabled: true,
        open: false
    },
    sleepCoach: {
        enabled: true,
        open: false
    },
    sleepResults: {
        enabled: true,
        open: false
    },
    sleep: {
        enabled: true,
        open: false
    },
    sleepScore: {
        enabled: true,
        open: false
    },
    sleepPulseOx: {
        enabled: true,
        open: false
    },
    allDayPulseOx: {
        enabled: true,
        open: false
    },
    hydration: {
        enabled: true,
        open: false
    },
    menstrualCycle: {
        enabled: true,
        open: false
    },
    respiration: {
        enabled: true,
        open: false
    },
    pregnancy: {
        enabled: true,
        open: false
    },
    bloodPressure: {
        enabled: true,
        open: false
    },
    trainingReadiness: {
        enabled: true,
        open: false
    },
    hrvStatus: {
        enabled: true,
        open: false
    },
    event: {
        enabled: true
    },
    jetLag: {
        enabled: true,
        open: false
    },
    pushes: {
        enabled: true,
        open: false
    }
};
